import type {
  ErpEmployeeAccountDto,
  ErpChangePasswordRequest,
  ErpLoginRequest,
  ErpLoginResponse,
  ErpMeResponse,
  ErpRefreshTokenRequest,
  ErpUpdateProfileRequest
} from "@/contracts";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:4000/api/v1";

type ApiErrorPayload = {
  message?: string | string[];
  error?: string;
  statusCode?: number;
};

export type ErpAuthSession = {
  accessToken: string;
  refreshToken?: string;
  accessTokenExpiresAt?: number;
  user?: ErpEmployeeAccountDto;
};

function joinUrl(baseUrl: string, path: string) {
  return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
}

function messageFromPayload(payload: unknown, fallback: string) {
  const errorPayload = payload as ApiErrorPayload;
  if (Array.isArray(errorPayload?.message))
    return errorPayload.message.join(", ");
  return errorPayload?.message || errorPayload?.error || fallback;
}

function isFormDataBody(body: BodyInit | null | undefined) {
  return typeof FormData !== "undefined" && body instanceof FormData;
}

async function readJson(response: Response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { token?: string } = {}
) {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");

  if (init.body && !isFormDataBody(init.body) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  if (init.token) {
    headers.set("Authorization", `Bearer ${init.token}`);
  }

  const response = await fetch(joinUrl(API_BASE_URL, path), {
    ...init,
    headers
  });
  const payload = await readJson(response);

  if (!response.ok) {
    throw new Error(messageFromPayload(payload, "Không thể kết nối hệ thống"));
  }

  return payload as T;
}

function getAccessToken(payload: ErpLoginResponse) {
  return (
    payload.accessToken ||
    payload.access_token ||
    payload.token ||
    payload.data?.accessToken ||
    payload.data?.access_token ||
    payload.data?.token
  );
}

function getRefreshToken(payload: ErpLoginResponse) {
  return (
    payload.refreshToken ||
    payload.refresh_token ||
    payload.data?.refreshToken ||
    payload.data?.refresh_token
  );
}

function getExpiresIn(payload: ErpLoginResponse) {
  return (
    payload.expiresIn ||
    payload.expires_in ||
    payload.data?.expiresIn ||
    payload.data?.expires_in
  );
}

export function normalizeErpAuthTokens(payload: ErpLoginResponse) {
  const accessToken = getAccessToken(payload);
  if (!accessToken) {
    throw new Error("Backend không trả access token hợp lệ");
  }

  const expiresIn = getExpiresIn(payload);
  return {
    accessToken,
    refreshToken: getRefreshToken(payload),
    accessTokenExpiresAt:
      typeof expiresIn === "number" && Number.isFinite(expiresIn)
        ? Date.now() + expiresIn * 1000
        : undefined
  };
}

function getUserFromLogin(payload: ErpLoginResponse) {
  return (
    payload.user ||
    payload.employee ||
    payload.account ||
    payload.data?.user ||
    payload.data?.employee ||
    payload.data?.account
  );
}

function getUserFromMe(payload: ErpMeResponse) {
  return payload.data || payload.user || payload.employee || payload.account;
}

function buildProfileFormData(payload: ErpUpdateProfileRequest) {
  const formData = new FormData();

  if (payload.fullName !== undefined) {
    formData.set("full_name", payload.fullName);
  }
  if (payload.phone !== undefined) {
    formData.set("phone", payload.phone);
  }
  if (payload.image) {
    formData.set("image", payload.image);
  }

  return formData;
}

export async function loginErp(
  payload: ErpLoginRequest
): Promise<ErpAuthSession> {
  const loginPayload = await request<ErpLoginResponse>("/erp/auth/login", {
    method: "POST",
    body: JSON.stringify(payload)
  });

  const tokens = normalizeErpAuthTokens(loginPayload);

  let user = getUserFromLogin(loginPayload);
  if (!user) {
    try {
      user = await getCurrentErpUser(tokens.accessToken);
    } catch {
      user = undefined;
    }
  }

  return {
    ...tokens,
    user
  };
}

export async function refreshErp(
  refreshToken: string
): Promise<ErpAuthSession> {
  const refreshPayload = await request<ErpLoginResponse>(
    "/erp/auth/refresh-token",
    {
      method: "POST",
      body: JSON.stringify({ refreshToken } satisfies ErpRefreshTokenRequest)
    }
  );

  return {
    ...normalizeErpAuthTokens(refreshPayload),
    user: getUserFromLogin(refreshPayload)
  };
}

export async function getCurrentErpUser(token: string) {
  const payload = await request<ErpMeResponse>("/erp/auth/me", {
    method: "GET",
    token
  });
  return getUserFromMe(payload);
}

export async function updateCurrentErpUserProfile(
  token: string,
  payload: ErpUpdateProfileRequest
) {
  const response = await request<ErpMeResponse>("/erp/auth/me", {
    method: "PATCH",
    token,
    body: buildProfileFormData(payload)
  });
  return getUserFromMe(response);
}

export async function changeErpPassword(
  token: string,
  payload: ErpChangePasswordRequest
) {
  return request<unknown>("/erp/auth/change-password", {
    method: "POST",
    token,
    body: JSON.stringify({
      current_password: payload.currentPassword,
      new_password: payload.newPassword
    })
  });
}

export async function logoutErp(token?: string) {
  if (!token) return;

  try {
    await request("/erp/auth/logout", {
      method: "POST",
      token
    });
  } catch {
    // Local session cleanup should still happen even if backend logout fails.
  }
}

export function getApiBaseUrl() {
  return API_BASE_URL;
}
